/**
 * Content Block Wrapper Component
 * Wraps edit-mode blocks with persistent chrome and actions.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCheck, faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import { useTheme, type TranslationRecordStatus } from '@course-authoring/shared-types';
import { useTwoClickConfirm } from '../hooks';
import { formatBlockHumanId } from '../utils/humanIds';
import { TranslationStatusBadge } from './TranslationStatusPrimitives';

/**
 * NA-437's toolbar icon is a pixel-exact copy of the approved prototype's
 * markup (https://copy-block-to-lesson.netlify.app/, `#btnCopy` svg) — a
 * document-with-export-arrow glyph that has no equivalent in Font Awesome's
 * free set. Hand-authored inline SVG only for this one glyph, per the
 * explicit "exactly match the prototype" instruction; every other icon in
 * this file stays on Font Awesome per CLAUDE.md.
 */
function CopyToAnotherLessonIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x={1.9} y={2.3} width={7.4} height={11.4} rx={1.4} />
      <path d="M8 8h6.2" />
      <path d="M12.2 6 14.2 8l-2 2" />
    </svg>
  );
}

interface ContentBlockWrapperProps {
  children: React.ReactNode;
  blockId: string;
  blockTypeLabel: string;
  blockDisplayTitle: string;
  isInteractionRequired?: boolean;
  isFirst: boolean;
  isLast: boolean;
  isLocked?: boolean;
  /**
   * Dims the content and makes it inert without the Locked badge — the
   * publish-lock treatment (NA-207), distinct from the per-block Emtrain lock.
   */
  contentInert?: boolean;
  /**
   * True when this block is an account `override` — edited in client context
   * and disconnected from the Emtrain standard version (NA-199). Shows the
   * persistent "Customized" status bar with a revert-to-standard action.
   */
  isCustomized?: boolean;
  /** Opens the revert-to-standard confirmation. Omitted hides the status bar's action. */
  onRevertToStandard?: () => void;
  /**
   * True when this block's anchor was created for the active account rather
   * than a base block — no Emtrain standard version exists for it, so it
   * gets its own status bar instead of "Customized" (no revert action).
   */
  isClientAdded?: boolean;
  onToggleLock?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onDuplicate?: () => void;
  /** NA-437: Emtrain-only — omitted in client-account context. */
  onCopyToAnotherLesson?: () => void;
  /** NA-437: highlight ring shown after this block was copied here via "Open that lesson after copying". */
  isHighlighted?: boolean;
  /** Omitted (e.g. locked blocks) hides the delete/hide affordance entirely. */
  onDelete?: () => void;
  /**
   * 'delete' (default) removes the block (two-click confirm). 'hide' suppresses
   * it for the active client — a single click, reversible from the blocks list.
   */
  deleteMode?: 'delete' | 'hide';
  onOpenSettings?: () => void;
  onPreviewFromHere?: () => void;
  translationStatus?: TranslationRecordStatus | null;
}

export function ContentBlockWrapper({
  children,
  blockId,
  blockTypeLabel,
  blockDisplayTitle,
  isInteractionRequired = false,
  isFirst,
  isLast,
  isLocked = false,
  contentInert = false,
  isCustomized = false,
  onRevertToStandard,
  isClientAdded = false,
  onToggleLock,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onCopyToAnotherLesson,
  isHighlighted = false,
  onDelete,
  deleteMode = 'delete',
  onOpenSettings,
  onPreviewFromHere,
  translationStatus,
}: ContentBlockWrapperProps) {
  const { tokens } = useTheme();
  const { armed: showDeleteConfirm, trigger: triggerDeleteConfirm, reset: resetDeleteConfirm } =
    useTwoClickConfirm(() => onDelete?.());
  const blockHumanId = formatBlockHumanId(blockId);

  const styles: Record<string, React.CSSProperties> = {
    wrapper: {
      position: 'relative',
      minWidth: 0,
    },
    card: {
      minWidth: 0,
      borderRadius: tokens.radius.lg,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.surface,
      boxShadow: tokens.shadow.sm,
      overflow: 'hidden',
      // NA-437: matches the prototype's `.block` transition timing exactly
      // (this property is otherwise inert — it only activates when
      // isHighlighted flips, which didn't exist before this feature).
      transition: 'border-color 0.5s ease, box-shadow 0.5s ease',
    },
    // NA-437: exact values from the prototype's `.block.landed` state.
    // `border` here (not the `borderColor` longhand) deliberately matches
    // `card.border`'s own shorthand key — mixing shorthand and longhand
    // border properties across the two style objects left a stale
    // `border-color` behind once React removed the highlighted style being
    // spread on top (the shorthand's color and the separately-set longhand
    // don't share one CSSOM slot), which reverted the block to a black
    // border for the rest of the session once the highlight cleared. Using
    // the same shorthand key on both means React always replaces the whole
    // `border` property, so clearing the highlight fully restores `card`'s.
    cardHighlighted: {
      border: '1px solid #a9bdf6',
      boxShadow: '0 0 0 3px rgba(31, 79, 216, 0.13)',
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: tokens.spacing.lg,
      padding: `${tokens.spacing.lg} ${tokens.spacing.xl}`,
      backgroundColor: tokens.backgrounds.page,
      borderBottom: `1px solid ${tokens.borders.default}`,
      flexWrap: 'wrap',
      boxSizing: 'border-box',
    },
    titleGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xs,
      minWidth: 0,
      flex: '1 1 280px',
    },
    typeLabel: {
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: tokens.text.subtle,
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      flexWrap: 'wrap',
      minWidth: 0,
    },
    title: {
      margin: 0,
      fontSize: '16px',
      lineHeight: 1.4,
      fontWeight: 600,
      color: tokens.text.primary,
      wordBreak: 'break-word',
    },
    interactionBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '26px',
      padding: `0 ${tokens.spacing.md}`,
      borderRadius: tokens.radius.full,
      border: `1px solid ${tokens.brand.primary}`,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.brand.primary,
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    interactionBadgeStatic: {
      cursor: 'default',
    },
    lockedBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: tokens.spacing.xs,
      minHeight: '26px',
      padding: `0 ${tokens.spacing.md}`,
      borderRadius: tokens.radius.full,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.surfaceAlt,
      color: tokens.text.muted,
      fontSize: '12px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: tokens.spacing.sm,
      flexWrap: 'wrap',
      flex: '0 0 auto',
    },
    actionButton: {
      width: '32px',
      height: '32px',
      padding: 0,
      fontSize: '13px',
      color: tokens.text.muted,
      backgroundColor: tokens.backgrounds.surface,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: `background-color ${tokens.ui.transitionDefault}, color ${tokens.ui.transitionDefault}`,
      boxShadow: tokens.shadow.sm,
      fontFamily: tokens.typography.fontFamily,
    },
    actionButtonDisabled: {
      opacity: 0.3,
      cursor: 'not-allowed',
    },
    lockToggleLocked: {
      backgroundColor: tokens.backgrounds.surfaceAlt,
      color: tokens.text.primary,
    },
    // NA-437: exact colors from the approved prototype's `.tbtn.accent` class
    // (https://copy-block-to-lesson.netlify.app/) — a deliberate one-off,
    // scoped to only this button, not the shared `actionButton` base every
    // other toolbar icon (lock/move/duplicate/preview/delete) still uses.
    copyToAnotherLessonButton: {
      backgroundColor: '#eef2fe',
      color: '#1f4fd8',
      border: '1px solid #c9d6fb',
    },
    deleteButton: {
      color: tokens.ui.errorText,
    },
    deleteButtonConfirm: {
      backgroundColor: tokens.ui.errorText,
      color: tokens.text.onInverse,
      border: `1px solid ${tokens.ui.errorText}`,
    },
    content: {
      minWidth: 0,
      padding: tokens.spacing.xl,
      backgroundColor: tokens.backgrounds.surface,
      boxSizing: 'border-box',
    },
    inertGroup: {
      minWidth: 0,
    },
    customizedBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: tokens.spacing.md,
      flexWrap: 'wrap',
      padding: `${tokens.spacing.sm} ${tokens.spacing.xl}`,
      backgroundColor: tokens.banner.info.background,
      borderBottom: `1px solid ${tokens.borders.default}`,
      boxSizing: 'border-box',
    },
    customizedBarText: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      flexWrap: 'wrap',
      fontSize: '13px',
      lineHeight: 1.4,
      color: tokens.text.primary,
    },
    customizedBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: `2px ${tokens.spacing.sm}`,
      borderRadius: tokens.radius.full,
      border: `1px solid ${tokens.banner.info.border}`,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.banner.info.border,
      fontSize: '12px',
      fontWeight: 700,
      whiteSpace: 'nowrap',
    },
    revertLink: {
      background: 'none',
      border: 'none',
      padding: 0,
      color: tokens.text.link,
      fontSize: '13px',
      fontWeight: 600,
      textDecoration: 'underline',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    clientAddedBar: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      flexWrap: 'wrap',
      padding: `${tokens.spacing.sm} ${tokens.spacing.xl}`,
      backgroundColor: '#EDE9F7',
      borderBottom: `1px solid ${tokens.borders.default}`,
      boxSizing: 'border-box',
      fontSize: '13px',
      lineHeight: 1.4,
      color: '#4A3B7A',
    },
    clientAddedBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: `2px ${tokens.spacing.sm}`,
      borderRadius: tokens.radius.full,
      border: '1px solid #4A3B7A',
      backgroundColor: '#dccff2',
      color: '#4A3B7A',
      fontSize: '12px',
      fontWeight: 700,
      whiteSpace: 'nowrap',
    },
  };

  const handleDelete = () => {
    if (!onDelete) {
      return;
    }
    // Hide is non-destructive and reversible (re-enable from the blocks list),
    // so it acts on a single click without the delete confirm step.
    if (deleteMode === 'hide') {
      onDelete();
      return;
    }
    triggerDeleteConfirm();
  };

  return (
    <div
      style={styles.wrapper}
      onMouseLeave={resetDeleteConfirm}
      data-block-id={blockId}
    >
      <div style={{ ...styles.card, ...(isHighlighted ? styles.cardHighlighted : {}) }}>
        <div style={styles.header}>
          <div style={styles.titleGroup}>
            <span style={styles.typeLabel}>
              {blockHumanId ? `${blockTypeLabel} · ${blockHumanId}` : blockTypeLabel}
            </span>
            <div style={styles.titleRow}>
              <h3 style={styles.title}>{blockDisplayTitle}</h3>
              {translationStatus ? (
                <TranslationStatusBadge status={translationStatus} missingLabel="Not Translated" />
              ) : null}
              {isLocked && (
                <span style={styles.lockedBadge}>
                  <FontAwesomeIcon icon={faLock} style={{ fontSize: '11px' }} />
                  Locked
                </span>
              )}
              {isInteractionRequired && (
                onOpenSettings ? (
                  <button
                    type="button"
                    onClick={onOpenSettings}
                    style={styles.interactionBadge}
                    aria-label="Open interaction settings"
                    title="Open block settings"
                  >
                    Interaction required
                  </button>
                ) : (
                  <span
                    style={{
                      ...styles.interactionBadge,
                      ...styles.interactionBadgeStatic,
                    }}
                  >
                    Interaction required
                  </span>
                )
              )}
            </div>
          </div>

          <div style={styles.actions}>
            {onToggleLock ? (
              <button
                type="button"
                onClick={onToggleLock}
                style={{
                  ...styles.actionButton,
                  ...(isLocked ? styles.lockToggleLocked : {}),
                }}
                title={isLocked ? 'Unlock block' : 'Lock block'}
                aria-label={isLocked ? 'Unlock block' : 'Lock block'}
                aria-pressed={isLocked}
              >
                <FontAwesomeIcon icon={isLocked ? faLock : faLockOpen} />
              </button>
            ) : null}
            {onMoveUp ? (
              <button
                type="button"
                onClick={onMoveUp}
                disabled={isFirst}
                style={{
                  ...styles.actionButton,
                  ...(isFirst ? styles.actionButtonDisabled : {}),
                }}
                title="Move up"
                aria-label="Move block up"
              >
                ↑
              </button>
            ) : null}
            {onMoveDown ? (
              <button
                type="button"
                onClick={onMoveDown}
                disabled={isLast}
                style={{
                  ...styles.actionButton,
                  ...(isLast ? styles.actionButtonDisabled : {}),
                }}
                title="Move down"
                aria-label="Move block down"
              >
                ↓
              </button>
            ) : null}
            {onDuplicate ? (
              <button
                type="button"
                onClick={onDuplicate}
                style={styles.actionButton}
                title="Duplicate in this lesson"
                aria-label="Duplicate in this lesson"
              >
                ⧉
              </button>
            ) : null}
            {onCopyToAnotherLesson ? (
              <button
                type="button"
                onClick={onCopyToAnotherLesson}
                style={{ ...styles.actionButton, ...styles.copyToAnotherLessonButton }}
                title="Copy to another lesson"
                aria-label="Copy block to another lesson"
                onMouseEnter={(event) => { event.currentTarget.style.backgroundColor = '#e2e9fd'; }}
                onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = '#eef2fe'; }}
              >
                <CopyToAnotherLessonIcon />
              </button>
            ) : null}
            {onPreviewFromHere && (
              <button
                type="button"
                onClick={onPreviewFromHere}
                style={styles.actionButton}
                title="Preview from here"
                aria-label="Preview from this block"
              >
                👁️
              </button>
            )}
            {onOpenSettings && (
              <button
                type="button"
                onClick={onOpenSettings}
                style={styles.actionButton}
                title="Settings"
                aria-label="Block settings"
              >
                ⚙️
              </button>
            )}
            {onDelete ? (
              <button
                type="button"
                onClick={handleDelete}
                style={{
                  ...styles.actionButton,
                  // Both hide (⊘) and delete (✕) use the red/error color.
                  ...styles.deleteButton,
                  ...(deleteMode !== 'hide' && showDeleteConfirm ? styles.deleteButtonConfirm : {}),
                }}
                title={
                  deleteMode === 'hide'
                    ? 'Hide for this client (re-enable from the blocks list)'
                    : showDeleteConfirm ? 'Click again to confirm' : 'Delete'
                }
                aria-label={deleteMode === 'hide' ? 'Hide block for this client' : 'Delete block'}
              >
                <FontAwesomeIcon
                  icon={deleteMode !== 'hide' && showDeleteConfirm ? faCheck : faXmark}
                />
              </button>
            ) : null}
          </div>
        </div>

        <div
          style={{ ...styles.inertGroup, ...(isLocked || contentInert ? { opacity: 0.6 } : {}) }}
          inert={isLocked || contentInert || undefined}
        >
          {isCustomized && (
            <div style={styles.customizedBar}>
              <span style={styles.customizedBarText}>
                <span style={styles.customizedBadge}>Customized</span>
                This block has been disconnected from the Emtrain standard version. It will not
                receive updates made by Emtrain.
              </span>
              {onRevertToStandard && (
                <button type="button" style={styles.revertLink} onClick={onRevertToStandard}>
                  Revert to standard
                </button>
              )}
            </div>
          )}
          {isClientAdded && (
            <div style={styles.clientAddedBar}>
              <span style={styles.clientAddedBadge}>Account-added</span>
              This block was added for this account and doesn’t exist in the Emtrain standard
              version.
            </div>
          )}
          <div style={styles.content}>{children}</div>
        </div>
      </div>
    </div>
  );
}
